export type ProviderListingsQueryStatus = {
    isError: boolean;
    isFetchNextPageError: boolean;
    isFetchPreviousPageError: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    isFetchingPreviousPage: boolean;
    data: object | undefined;
};

export type ProviderListingsPanelState =
    | "failed"
    | "no-matches"
    | "loading"
    | "stale"
    | "ready";

export type ProviderListingsQueryFlagsInput = {
    query: ProviderListingsQueryStatus;
    isPlaceholderData: boolean;
    isResettingQueryWindow: boolean;
    hasQuery: boolean;
    currentPageCardsLength: number;
    listingHydrationIsError: boolean;
    listingHydrationIsPending: boolean;
};

export type ProviderListingsQueryFlags = {
    hasPageError: boolean;
    isPageLoading: boolean;
    isSearching: boolean;
    panelState: ProviderListingsPanelState;
};

function resolvePanelState({
    hasPageError,
    isPageLoading,
    isSearching,
    isPlaceholderData,
    hasQuery,
    currentPageCardsLength,
}: {
    hasPageError: boolean;
    isPageLoading: boolean;
    isSearching: boolean;
    isPlaceholderData: boolean;
    hasQuery: boolean;
    currentPageCardsLength: number;
}): ProviderListingsPanelState {
    if (hasPageError && currentPageCardsLength === 0) return "failed";
    const isStale = isPlaceholderData && !isPageLoading;
    if (hasPageError) return isStale ? "stale" : "ready";
    if (isPageLoading) return "loading";
    if (hasQuery && !isSearching && currentPageCardsLength === 0) {
        return "no-matches";
    }
    return isStale ? "stale" : "ready";
}

export function deriveProviderListingsQueryFlags({
    query,
    isPlaceholderData,
    isResettingQueryWindow,
    hasQuery,
    currentPageCardsLength,
    listingHydrationIsError,
    listingHydrationIsPending,
}: ProviderListingsQueryFlagsInput): ProviderListingsQueryFlags {
    const hasPageError =
        query.isError ||
        query.isFetchNextPageError ||
        query.isFetchPreviousPageError ||
        listingHydrationIsError;
    const isLoadingCurrentQuery =
        (query.isFetching && query.data === undefined) ||
        isResettingQueryWindow;
    const isPageLoading =
        query.isFetchingNextPage ||
        query.isFetchingPreviousPage ||
        isLoadingCurrentQuery ||
        listingHydrationIsPending;
    const isSearching =
        isPlaceholderData || (hasQuery && isLoadingCurrentQuery);

    return {
        hasPageError,
        isPageLoading,
        isSearching,
        panelState: resolvePanelState({
            hasPageError,
            isPageLoading,
            isSearching,
            isPlaceholderData,
            hasQuery,
            currentPageCardsLength,
        }),
    };
}
